import { PUBLIC_IS_PREVIEW_DEPLOYMENT } from '$env/static/public';
import type { ToastContainerType } from '$lib/types/toast';
import type { SuperformsMessageType } from '$lib/types/superformsMessageType';
import { browser } from '$app/environment';
import { type PropertiesContext, type Property } from '$lib/context';

export const isPreviewDeployment = PUBLIC_IS_PREVIEW_DEPLOYMENT === '1';

export function hasOwnProperty<X, Y extends PropertyKey>(obj: X, prop: Y): obj is NonNullable<X> & Record<Y, unknown> {
    return typeof obj !== 'undefined' && obj !== null && Object.hasOwn(obj as object, prop);
}

export function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}

export function preventEvent(e: Event): void {
    e.preventDefault();
}

export function exclude<T, K extends keyof T>(obj: T, ...keys: K[]): Omit<T, K> {
    if (!obj) {
        return obj;
    }

    const names = Object.getOwnPropertyNames(obj) as unknown as K[];
    const newObj = {};

    for (let i = 0; i < names.length; i++) {
        const name = names[i];

        if (keys.indexOf(name) !== -1) {
            continue;
        }

        // @ts-ignore
        newObj[name] = obj[name];
    }

    return newObj as Omit<T, K>;
}

export function range(start: number, stop: number, step = 1): number[] {
    return Array(Math.ceil((stop - start) / step))
        .fill(start)
        .map((x, y) => x + y * step);
}

/**
 * Convert all words longer than 3 characters found in the string to "Title Case"
 * @param str the string to convert
 */
export function convertTitleCase(str: string): string {
    return str.replace(/\w{4,}/g, (s) => s.slice(0, 1).toUpperCase() + s.slice(1).toLowerCase());
}

export function sendFormToasts(toast: ToastContainerType, message: SuperformsMessageType | undefined) {
    // TODO: MORITZ fix showError undefined when account creation errors

    console.log(message);

    if (message?.text) {
        if (message.type === 'general') {
            toast.showMessage(message.text);
        } else if (message.type === 'success') {
            toast.showSuccess(message.text);
        } else if (message.type === 'warning') {
            toast.showWarning(message.text);
        } else if (message.type === 'error') {
            toast.showError(message.text);
        }
    } else {
        console.error('sendFormToasts error: message undefined');
    }
}

export function showLoadingDialog() {
    const dialog = document.getElementById('loading-dialog') as HTMLDialogElement;
    dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
    });
    dialog.addEventListener('click', (event) => {
        event.stopPropagation();
    });

    dialog.showModal();
}

export function closeLoadingDialog() {
    const dialog = document.getElementById('loading-dialog') as HTMLDialogElement;
    dialog.close();
}

export function groupByKeySingle<T, KeyType extends string | number | symbol = string>(
    array: T[],
    key: keyof T
): Record<KeyType, T> {
    // @ts-ignore
    return array.reduce((hash, object) => {
        if (object[key] === undefined) {
            return hash;
        }

        // @ts-ignore
        return Object.assign(hash, { [object[key]]: object });
    }, {});
}

export function disableGlobalScroll(state: boolean) {
    if (browser) {
        if (state) {
            document.body.classList.add('no-scroll');
        } else {
            document.body.classList.remove('no-scroll');
        }
    }
}

/**
 * Returns the matching id of an option in string format, if no id exists it returns an empty string
 * @param property
 * @param name
 * @return id
 */
export function getIdByOptionName(property: Property, name: string): string {
    let id = '';

    if (!property?.options) return '';

    Object.entries(property.options).forEach(([key, translations]) => {
        if (Object.values(translations).some((value) => value.toLowerCase() === name.toLowerCase())) {
            id = key;
        }
    });

    return id;
}
